import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefaultButton, Spinner, SpinnerSize } from '@fluentui/react'

import {
  getMarketJobs,
  getMarketJobDetail,
  assessMarketJob,
  makeClientFromMarketJob,
  getMarketJobCandidateMatches,
  MarketJobSummary,
  MarketJobDetail,
  MatchResult,
} from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './MarketJobs.module.css'

type View = 'list' | 'detail' | 'matches'
type Filter = 'new' | 'all'

const SOURCE_STYLES: Record<string, string> = {
  seek: styles.sourceSeek,
  indeed: styles.sourceIndeed,
  careerjet: styles.sourceCareerjet,
  jora: styles.sourceJora,
}

const getSourceClass = (source: string) => {
  if (SOURCE_STYLES[source]) return SOURCE_STYLES[source]
  if (source.includes('_careers') || source.includes('_career')) return styles.sourceManufacturer
  return styles.sourceDefault
}

const getSourceLabel = (source: string) => {
  const labels: Record<string, string> = {
    seek: 'Seek',
    indeed: 'Indeed',
    careerjet: 'CareerJet',
    jora: 'Jora',
    bmw_careers: 'BMW',
    toyota_careers: 'Toyota',
    kia_careers: 'Kia',
    hyundai_careers: 'Hyundai',
    mazda_careers: 'Mazda',
    honda_careers: 'Honda',
    nissan_careers: 'Nissan',
    mercedes_careers: 'Mercedes',
    vw_careers: 'VW',
    ford_careers: 'Ford',
    subaru_careers: 'Subaru',
    mitsubishi_careers: 'Mitsubishi',
    eagers_careers: 'Eagers',
    peterwarren_careers: 'Peter Warren',
    autosports_careers: 'AutoSports',
  }
  return labels[source] || source
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return dateStr.slice(0, 10)
  }
}

const MarketJobs = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const [view, setView] = useState<View>('list')
  const [filter, setFilter] = useState<Filter>('new')
  const [jobs, setJobs] = useState<MarketJobSummary[]>([])
  const [selectedJob, setSelectedJob] = useState<MarketJobDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showMakeClientModal, setShowMakeClientModal] = useState(false)
  const [clientFormData, setClientFormData] = useState<Record<string, string>>({})
  const [makeClientLoading, setMakeClientLoading] = useState(false)
  const [matchJob, setMatchJob] = useState<MarketJobSummary | null>(null)
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null)
  const [matchLoading, setMatchLoading] = useState(false)

  const viewRef = useRef(view)
  const selectedJobRef = useRef(selectedJob)
  viewRef.current = view
  selectedJobRef.current = selectedJob

  useEffect(() => {
    loadJobs()
  }, [filter])

  useEffect(() => {
    const handler = async () => {
      loadJobs()
      if (viewRef.current === 'detail' && selectedJobRef.current) {
        const detail = await getMarketJobDetail(selectedJobRef.current.marketjobid)
        if (detail) setSelectedJob(detail)
      }
    }
    window.addEventListener('app-data-changed', handler)
    return () => window.removeEventListener('app-data-changed', handler)
  }, [filter])

  useEffect(() => {
    let context = `User is viewing Market Intelligence.`
    if (view === 'list') {
      context += ` ${jobs.length} market jobs listed (filter: ${filter}).`
    } else if (view === 'detail' && selectedJob) {
      context += ` Viewing details for: ${selectedJob.job_title} at ${selectedJob.company_name || 'unknown company'}.`
    } else if (view === 'matches' && matchJob) {
      context += ` Viewing candidate matches for market job: ${matchJob.job_title}.`
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: context })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [view, jobs.length, filter, selectedJob, matchJob])

  const loadJobs = async () => {
    setLoading(true)
    setError(null)
    const status = filter === 'new' ? 'new' : undefined
    const data = await getMarketJobs(status)
    setJobs(data)
    setLoading(false)
  }

  const handleViewDetail = async (marketjobid: number) => {
    setDetailLoading(true)
    setError(null)
    setView('detail')
    const detail = await getMarketJobDetail(marketjobid)
    if (detail) {
      setSelectedJob(detail)
    } else {
      setError('Failed to load job details')
    }
    setDetailLoading(false)
  }

  const handleAssess = async (marketjobid: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    const success = await assessMarketJob(marketjobid)
    if (success) {
      // Remove from list if filtering by 'new'
      if (filter === 'new') {
        setJobs(prev => prev.filter(j => j.marketjobid !== marketjobid))
      } else {
        setJobs(prev => prev.map(j =>
          j.marketjobid === marketjobid ? { ...j, status: 'assessed' } : j
        ))
      }
      // Update detail view if open
      if (selectedJob && selectedJob.marketjobid === marketjobid) {
        setSelectedJob({ ...selectedJob, status: 'assessed' })
      }
    }
  }

  const handleOpenMakeClient = () => {
    if (!selectedJob) return
    setClientFormData({
      company_name: selectedJob.company_name || '',
      contact_name: selectedJob.contact_name || '',
      contact_email: selectedJob.contact_email || '',
      contact_phone: selectedJob.contact_phone || '',
      location: selectedJob.location || '',
      industry: 'Automotive',
      website: '',
      notes: `Converted from market intelligence (${getSourceLabel(selectedJob.source)})`,
    })
    setShowMakeClientModal(true)
  }

  const handleMakeClient = async () => {
    if (!selectedJob) return
    setMakeClientLoading(true)
    const result = await makeClientFromMarketJob(selectedJob.marketjobid, clientFormData)
    setMakeClientLoading(false)
    if (result.success) {
      setShowMakeClientModal(false)
      setSelectedJob({ ...selectedJob, status: 'converted', clientid: result.clientid ?? null })
      // Update list
      setJobs(prev => prev.map(j =>
        j.marketjobid === selectedJob.marketjobid ? { ...j, status: 'converted' } : j
      ))
    } else {
      setError(result.error || 'Failed to create client')
    }
  }

  const handleFindCandidates = async (job: MarketJobSummary, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setMatchJob(job)
    setMatchResult(null)
    setError(null)
    setView('matches')
    setMatchLoading(true)
    try {
      const result = await getMarketJobCandidateMatches(job.marketjobid)
      setMatchResult(result)
    } catch (err: any) {
      setError(err.message || 'Failed to find matching candidates. Please try again.')
    } finally {
      setMatchLoading(false)
    }
  }

  const handleBack = () => {
    if (view === 'detail') {
      setSelectedJob(null)
      setView('list')
    } else if (view === 'matches') {
      setMatchJob(null)
      setMatchResult(null)
      setError(null)
      setView('list')
    } else {
      navigate('/')
    }
  }

  const getScoreClass = (score: number) => {
    if (score >= 70) return styles.scoreHigh
    if (score >= 40) return styles.scoreMedium
    return styles.scoreLow
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new': return styles.statusNew
      case 'assessed': return styles.statusAssessed
      case 'converted': return styles.statusConverted
      default: return ''
    }
  }

  const renderListView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={() => navigate('/')}
          title="Back to chat"
        />
        <h1 className={styles.title}>Market Intelligence</h1>
      </div>

      <div className={styles.filterBar}>
        <button
          className={`${styles.filterTab} ${filter === 'new' ? styles.filterTabActive : ''}`}
          onClick={() => setFilter('new')}
        >
          New
        </button>
        <button
          className={`${styles.filterTab} ${filter === 'all' ? styles.filterTabActive : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading market jobs..." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : jobs.length === 0 ? (
        <div className={styles.emptyState}>
          {filter === 'new' ? 'No new market jobs found. Run the scraper to discover new opportunities.' : 'No market jobs found.'}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Company</th>
              <th>Job Title</th>
              <th>Location</th>
              <th>Contact</th>
              <th>Source</th>
              <th>Found</th>
              {filter === 'all' && <th>Status</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <tr key={job.marketjobid} onClick={() => handleViewDetail(job.marketjobid)}>
                <td>{job.company_name || '-'}</td>
                <td>{job.job_title}</td>
                <td>{job.location || '-'}</td>
                <td className={styles.contactCell}>
                  {job.contact_email && (
                    <a href={`mailto:${job.contact_email}`} onClick={e => e.stopPropagation()} title={job.contact_email}>
                      {job.contact_email}
                    </a>
                  )}
                  {job.contact_phone && (
                    <span className={styles.contactPhone} title={job.contact_phone}>{job.contact_phone}</span>
                  )}
                  {!job.contact_email && !job.contact_phone && '-'}
                </td>
                <td>
                  <span className={`${styles.sourceBadge} ${getSourceClass(job.source)}`}>
                    {getSourceLabel(job.source)}
                  </span>
                </td>
                <td>{formatDate(job.createdate)}</td>
                {filter === 'all' && (
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(job.status)}`}>
                      {job.status}
                    </span>
                  </td>
                )}
                <td>
                  <div className={styles.actionsCell}>
                    <button
                      className={styles.findButton}
                      onClick={(e) => handleFindCandidates(job, e)}
                      title="Find matching candidates"
                    >
                      Find Candidates
                    </button>
                    {job.status === 'new' && (
                      <button
                        className={styles.assessButton}
                        onClick={(e) => handleAssess(job.marketjobid, e)}
                        title="Mark as assessed"
                      >
                        Mark Assessed
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  const renderDetailView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to list"
        />
        <h1 className={styles.title}>Job Details</h1>
      </div>

      {detailLoading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading details..." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : selectedJob ? (
        <div className={styles.detailCard}>
          <h2 className={styles.detailTitle}>{selectedJob.job_title}</h2>
          <p className={styles.detailCompany}>{selectedJob.company_name || 'Unknown Company'}</p>

          <div className={styles.detailMeta}>
            {selectedJob.location && (
              <span><span className={styles.detailMetaLabel}>Location:</span> {selectedJob.location}</span>
            )}
            {selectedJob.salary_range && (
              <span><span className={styles.detailMetaLabel}>Salary:</span> {selectedJob.salary_range}</span>
            )}
            <span>
              <span className={styles.detailMetaLabel}>Source:</span>{' '}
              <span className={`${styles.sourceBadge} ${getSourceClass(selectedJob.source)}`}>
                {getSourceLabel(selectedJob.source)}
              </span>
            </span>
            <span>
              <span className={styles.detailMetaLabel}>Status:</span>{' '}
              <span className={`${styles.statusBadge} ${getStatusBadgeClass(selectedJob.status)}`}>
                {selectedJob.status}
              </span>
            </span>
            <span><span className={styles.detailMetaLabel}>Found:</span> {formatDate(selectedJob.createdate)}</span>
          </div>

          {selectedJob.description && (
            <div className={styles.detailSection}>
              <h3 className={styles.detailSectionTitle}>Description</h3>
              <div className={styles.detailDescription}>{selectedJob.description}</div>
            </div>
          )}

          {(selectedJob.contact_name || selectedJob.contact_email || selectedJob.contact_phone) && (
            <div className={styles.detailSection}>
              <h3 className={styles.detailSectionTitle}>Contact Details</h3>
              <div className={styles.detailMeta}>
                {selectedJob.contact_name && <span><span className={styles.detailMetaLabel}>Name:</span> {selectedJob.contact_name}</span>}
                {selectedJob.contact_email && <span><span className={styles.detailMetaLabel}>Email:</span> {selectedJob.contact_email}</span>}
                {selectedJob.contact_phone && <span><span className={styles.detailMetaLabel}>Phone:</span> {selectedJob.contact_phone}</span>}
              </div>
            </div>
          )}

          {selectedJob.source_url && (
            <div className={styles.detailSection}>
              <a
                href={selectedJob.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.detailLink}
              >
                View Original Listing
              </a>
            </div>
          )}

          <div className={styles.detailActions}>
            {selectedJob.status === 'new' && (
              <button className={styles.secondaryButton} onClick={() => handleAssess(selectedJob.marketjobid)}>
                Mark as Assessed
              </button>
            )}
            {selectedJob.status !== 'converted' && (
              <button className={styles.primaryButton} onClick={handleOpenMakeClient}>
                Make Client
              </button>
            )}
            {selectedJob.status === 'converted' && selectedJob.clientid && (
              <span style={{ color: '#107c10', fontWeight: 600 }}>
                Converted to Client #{selectedJob.clientid}
              </span>
            )}
          </div>
        </div>
      ) : null}
    </>
  )

  const renderMatchesView = () => (
    <>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={handleBack}
          title="Back to list"
        />
        <h1 className={styles.title}>Candidate Matching</h1>
      </div>

      {matchJob && (
        <div className={styles.jobSummary}>
          <h2 className={styles.jobSummaryTitle}>{matchJob.job_title}</h2>
          <div className={styles.jobSummaryMeta}>
            {matchJob.company_name && <span>Company: {matchJob.company_name}</span>}
            {matchJob.location && <span>Location: {matchJob.location}</span>}
            {matchJob.salary_range && <span>Salary: {matchJob.salary_range}</span>}
            <span>
              Source:{' '}
              <span className={`${styles.sourceBadge} ${getSourceClass(matchJob.source)}`}>
                {getSourceLabel(matchJob.source)}
              </span>
            </span>
          </div>
        </div>
      )}

      {matchLoading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="AI is analyzing candidates... This may take a moment." />
        </div>
      ) : error ? (
        <div className={styles.errorState}>{error}</div>
      ) : matchResult ? (
        <>
          <h3 className={styles.sectionTitle}>Top Matching Candidates</h3>
          {matchResult.top_candidates.length === 0 ? (
            <div className={styles.emptyState}>No matching candidates found for this job.</div>
          ) : (
            <div className={styles.matchCards}>
              {matchResult.top_candidates.map((candidate, index) => (
                <div key={candidate.candidateid} className={styles.matchCard}>
                  <div className={styles.matchRank}>
                    <span className={`${styles.matchScore} ${getScoreClass(candidate.match_score)}`}>
                      {candidate.match_score}
                    </span>
                    <span className={styles.matchScoreLabel}>#{index + 1}</span>
                  </div>
                  <div className={styles.matchInfo}>
                    <h4 className={styles.matchName}>{candidate.name}</h4>
                    <div className={styles.matchMeta}>
                      {candidate.location && <span>{candidate.location}</span>}
                      {candidate.skills && <span> &middot; {candidate.skills}</span>}
                    </div>
                    {candidate.reasons && candidate.reasons.length > 0 && (
                      <ul className={styles.matchReasons}>
                        {candidate.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}
                    {candidate.distance_warning && (
                      <div className={styles.distanceWarning}>{candidate.distance_warning}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {matchResult.nearby_candidates.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Nearby Candidates</h3>
              <div className={styles.nearbyList}>
                {matchResult.nearby_candidates.map(candidate => (
                  <div key={candidate.candidateid} className={styles.nearbyChip}>
                    <span>{candidate.name}</span>
                    <span className={styles.nearbyNote}>
                      {candidate.distance_note || candidate.location}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : null}
    </>
  )

  const renderMakeClientModal = () => (
    <div className={styles.modalOverlay} onClick={() => setShowMakeClientModal(false)}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Create Client from Lead</h2>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Company Name</label>
          <input
            className={styles.formInput}
            value={clientFormData.company_name || ''}
            onChange={e => setClientFormData({ ...clientFormData, company_name: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Industry</label>
          <input
            className={styles.formInput}
            value={clientFormData.industry || ''}
            onChange={e => setClientFormData({ ...clientFormData, industry: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Contact Name</label>
          <input
            className={styles.formInput}
            value={clientFormData.contact_name || ''}
            onChange={e => setClientFormData({ ...clientFormData, contact_name: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Contact Email</label>
          <input
            className={styles.formInput}
            value={clientFormData.contact_email || ''}
            onChange={e => setClientFormData({ ...clientFormData, contact_email: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Contact Phone</label>
          <input
            className={styles.formInput}
            value={clientFormData.contact_phone || ''}
            onChange={e => setClientFormData({ ...clientFormData, contact_phone: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Location</label>
          <input
            className={styles.formInput}
            value={clientFormData.location || ''}
            onChange={e => setClientFormData({ ...clientFormData, location: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Website</label>
          <input
            className={styles.formInput}
            value={clientFormData.website || ''}
            onChange={e => setClientFormData({ ...clientFormData, website: e.target.value })}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Notes</label>
          <input
            className={styles.formInput}
            value={clientFormData.notes || ''}
            onChange={e => setClientFormData({ ...clientFormData, notes: e.target.value })}
          />
        </div>

        {error && <div className={styles.errorState}>{error}</div>}

        <div className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={() => setShowMakeClientModal(false)}
            disabled={makeClientLoading}
          >
            Cancel
          </button>
          <button
            className={styles.primaryButton}
            onClick={handleMakeClient}
            disabled={makeClientLoading || !clientFormData.company_name}
          >
            {makeClientLoading ? 'Creating...' : 'Create Client'}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className={styles.container}>
      {view === 'list' && renderListView()}
      {view === 'detail' && renderDetailView()}
      {view === 'matches' && renderMatchesView()}
      {showMakeClientModal && renderMakeClientModal()}
    </div>
  )
}

export default MarketJobs
